import { PaginatedResult, PaginationRequest, SystemTextMessages } from "@ews/react-data";
import Icon from "@ews/react-icons";
import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonCol, IonIcon, IonList, IonRow } from "@ionic/react";
import { checkmark, close, pencilOutline } from "ionicons/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { Locale } from "../../../../setup/locale";
import FilterList, { isFilterActive } from "../../../FilterList/FilterList";
import { FilterState } from "../../../FilterList/types";
import List from "../../../List/List";
import { defaultPageRange } from "../../../List/Pagination/pagerange";
import { HeaderOption } from "../../../List/types";
import { MenuLink } from "../../../Navigation/Sidebar/Sidebar";
import { useDeleteSystemTextMessages, useFetchSystemTextMessages } from "../../../ReactData/system";
import { Filter } from "../types/reportTypes";
import Modal from "./Modal/Modal";
import style from "./Phonenumbers.module.scss";
import { TextMessagesProps } from "./types";


type ListFilter = {
    telephoneNumber?: string;
};

type PersistentFilter = Filter<ListFilter> & FilterState;
const BooleanListItem = (value: boolean) =>
{
    return <IonIcon
        className={style.booleanButton}
        color={value ? "success" : "danger"}
        icon={value ? checkmark : close} />;
};

const standardFilter: Filter<ListFilter> = {
    sortCriteria: "telephoneNumber",
    sortOrder: "ASC",
    pageSize: defaultPageRange
};

const ignoreKeys: (keyof PersistentFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];

const header: HeaderOption<Locale, SystemTextMessages>[] = [
    { title: 'COM_FACPMANAGEMENT_PHONE_FIELD_PHONE_LABEL', key: 'telephoneNumber', both: true },
    { title: 'COM_ADMIN_USER_HEADING_NAME', key: 'label', both: true },
    { title: 'PLG_DEBUG_FIELD_LOG_PRIORITIES_ALERT', key: 'alarm', both: true, child: (item) => BooleanListItem(item.alarm) },
    { title: '[REACT_PRE_ALARM]', key: 'preAlarm', both: true, child: (item) => BooleanListItem(item.preAlarm) },
    { title: '[SID_MNU_VLI_LANGUAGE]', key: 'locale', both: true },
    { title: 'COM_FACPMANAGEMENT_CUSTOMER_FIELD_DATETIME_LABEL', key: 'dateTimeFormat', both: true },
];


const TextMessages: React.FC<TextMessagesProps> = ({
    system,
    filter,
    onIsOpen,
    onFilterActive
}) =>
{

    const { translate: t } = useL10n();


    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [currentPhoneNumber, setCurrentPhoneNumber] = useState<SystemTextMessages | null>(null);

    const [result, setList] = useState<PaginatedResult<SystemTextMessages>>({
        results: [
        ],
        currentPage: 0,
        pageSize: standardFilter.pageSize || defaultPageRange,
        totalCountOfEntries: 0,
        totalCountOfPages: 0
    });


    //const { generate } = useNamedRoutes();

    const [currentState, setState] = useState<PersistentFilter>(standardFilter);
    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);


    const fetch = useFetchSystemTextMessages();
    const deleteTextMessage = useDeleteSystemTextMessages();

    const getTextMessages = useCallback(async (id: string, filter: PaginationRequest & ListFilter) =>
    {
        const response = await fetch(id, filter);
        if (!response) return;
        setList(response);
    }, [setList, fetch]);

    const _delete = useCallback(async (data: SystemTextMessages) =>
    {
        if (data?.systemId && data?.id) {
            await deleteTextMessage(data.systemId, data.id);
            if (system?.id) {
                getTextMessages(system.id, currentState);
            }
        }
    }, [currentState, deleteTextMessage, getTextMessages, system.id]);

    const onPageChange = useCallback((page: PersistentFilter["page"]) => setState((prev) => ({ ...prev, page })), [setState]);
    const onSortChange = useCallback((sortCriteria: PersistentFilter["sortCriteria"], sortOrder: PersistentFilter["sortOrder"]) => setState((prev) => ({ ...prev, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistentFilter["pageSize"]) => setState((prev) => ({ ...prev, pageSize })), [setState]);
    const onReloadChange = useCallback(() => setState((prev) => ({ ...prev })), [setState]);


    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onIsDetailFilterOpen = useCallback((isDetailOpen: boolean) => onIsOpen && onIsOpen(isDetailOpen, "detail"), [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState]);

    const isFilterChanged = isFilterActive<PersistentFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);


    useEffect(() =>
    {
        if (!system?.id) return;

        getTextMessages(system.id, currentState);

    }, [currentState, getTextMessages, system]);


    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);

    const onMenu = useCallback((id: string, onMenuToggle: () => void) => <>
        <div></div>
        <IonList>

            <MenuLink
                title="COM_LOCALISE_ACCESS_EDIT"
                icon={pencilOutline}
                onClick={() =>
                {
                    const foundedPhoneData = result?.results?.find(data => data.id === id);

                    if (foundedPhoneData) {
                        setCurrentPhoneNumber(foundedPhoneData);
                        setIsOpen(true);
                    };
                    onMenuToggle();
                    return null;
                }} />
            <MenuLink title="COM_LOCALISE_ACCESS_DELETE" icon={<Icon icon="delete" />} onClick={() =>
            {
                const foundedPhoneData = result?.results?.find(data => data.id === id);

                if (foundedPhoneData) {
                    _delete(foundedPhoneData);
                };
                onMenuToggle();
                return null;
            }} />
        </IonList></>, [_delete, result?.results]);


    return <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

        <FilterList
            isOpen={filter?.isFilterOpen || false}
            isDetailsOpen={filter?.isDetailOpen || false}
            onIsOpen={onIsFilterOpen}
            onDetailOpen={onIsDetailFilterOpen}
            onReset={onResetFilter}
            noPrint
        >
            <IonCol >
                <IonRow class="ion-justify-content-end">
                    <IonButton
                        color={'success'}
                        onClick={() => setIsOpen(true)}
                    >
                        {t('COM_FACPMANAGEMENT_PHONE_HEADLINE_NEW')}
                    </IonButton>
                </IonRow>
            </IonCol>
        </FilterList>


        <List
            rows={result.results}
            sort={sortCriteria.current}
            pageSize={result.pageSize || 5}
            onSort={onSortChange}
            onPageChange={onPageChange}
            onPageSize={onPageSizeChange}
            onReload={onReloadChange}
            currentPage={result.currentPage}
            numberOfPages={result.totalCountOfPages}
            //link={{ url: "system:events", paramsKey: "id" }}
            noLink

            onMenu={onMenu}
            headers={header}
        />
        {/* <ModalButton
            vertical="bottom"
            horizontal="end"
            value={true}
            icon={add}
            color={"success"}
            onClick={() => setIsOpen(true)}
        /> */}
        <Modal //ZULETZT
            title={currentPhoneNumber ? "COM_FACPMANAGEMENT_PHONE_HEADLINE_UPDATE" : "COM_FACPMANAGEMENT_PHONE_HEADLINE_NEW"}
            isOpen={isOpen}
            onUpdate={() =>
            {
                if (system?.id) {
                    getTextMessages(system.id, currentState);
                }
            }}
            onDismiss={() =>
            {
                setCurrentPhoneNumber(null);
                setIsOpen(false);
            }}
            type={currentPhoneNumber ? "edit" : "new"}
            data={currentPhoneNumber || undefined}
            systemId={system.id}
        />
    </div>;
};

export default TextMessages;;
